import { useEffect, useMemo, useRef, useState } from "react";
import {
  Checkbox,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from "@chakra-ui/react";
import { FiDownload } from "react-icons/fi";
import CDS from "design-system";

import { csvExportUrl } from "../api";
import { useCustomFormQuery } from "../hooks";

// Keys/labels duplicated in Services::CustomFormResponsesCsv::META_COLUMNS — keep in sync.
const META_COLUMNS = [
  { key: "submitted_at", label: "Submitted At" },
  { key: "new_hire_name", label: "New Hire Name" },
  { key: "new_hire_email", label: "New Hire Email" },
  { key: "submitted_by", label: "Submitted By" },
  { key: "submitted_by_email", label: "Submitted By Email" },
] as const;

interface Props {
  formId: number | string;
  isOpen: boolean;
  onClose: () => void;
}

export default function ExportCsvDialog({ formId, isOpen, onClose }: Props) {
  const { data: form } = useCustomFormQuery(isOpen ? formId : null);
  const fieldOptions = useMemo(
    () => (form?.customFormFields ?? []).map((f) => ({ key: `field:${f.uuid}`, label: f.label })),
    [form],
  );
  const archivedFieldOptions = useMemo(
    () => (form?.archivedReferencedFields ?? []).map((f) => ({ key: `field:${f.uuid}`, label: `${f.label} (archived)` })),
    [form],
  );

  const defaultCheckedKeys = useMemo(
    () => [...META_COLUMNS.map((c) => c.key), ...fieldOptions.map((f) => f.key)],
    [fieldOptions],
  );
  const allKeys = useMemo(
    () => [...defaultCheckedKeys, ...archivedFieldOptions.map((f) => f.key)],
    [defaultCheckedKeys, archivedFieldOptions],
  );

  // Archived-referenced fields ship unchecked so current-shape exports don't change.
  const [selected, setSelected] = useState<Set<string>>(new Set(defaultCheckedKeys));

  // Re-seed only on the rising edge of `isOpen` — parent re-renders while
  // the dialog is already open should not wipe the user's current selection.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setSelected(new Set(defaultCheckedKeys));
    }
    wasOpen.current = isOpen;
  }, [isOpen, defaultCheckedKeys]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(allKeys));
  const selectNone = () => setSelected(new Set());

  const orderedSelection = allKeys.filter((k) => selected.has(k));
  const canDownload = orderedSelection.length > 0;

  const handleDownload = () => {
    if (!canDownload) return;
    const link = document.createElement("a");
    link.href = csvExportUrl(formId, orderedSelection);
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Export CSV</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <CDS.Text12 color="fg.subtle">
              Pick the columns to include in the CSV.
            </CDS.Text12>

            <HStack spacing={2}>
              <CDS.Button intent={CDS.Button.intent.secondary} variant={CDS.Button.variant.ghost} size="sm" onClick={selectAll}>
                Select all
              </CDS.Button>
              <CDS.Button intent={CDS.Button.intent.secondary} variant={CDS.Button.variant.ghost} size="sm" onClick={selectNone}>
                Select none
              </CDS.Button>
            </HStack>

            <Stack spacing={2}>
              {META_COLUMNS.map((col) => (
                <Checkbox
                  key={col.key}
                  isChecked={selected.has(col.key)}
                  onChange={() => toggle(col.key)}
                >
                  {col.label}
                </Checkbox>
              ))}
            </Stack>

            {fieldOptions.length > 0 && (
              <>
                <Divider />
                <CDS.Text10 color="fg.subtle" fontWeight="bold" textTransform="uppercase">
                  Form fields
                </CDS.Text10>
                <Stack spacing={2}>
                  {fieldOptions.map((f) => (
                    <Checkbox
                      key={f.key}
                      isChecked={selected.has(f.key)}
                      onChange={() => toggle(f.key)}
                    >
                      {f.label}
                    </Checkbox>
                  ))}
                </Stack>
              </>
            )}

            {archivedFieldOptions.length > 0 && (
              <>
                <Divider />
                <CDS.Text10 color="fg.subtle" fontWeight="bold" textTransform="uppercase">
                  Archived fields (historical answers)
                </CDS.Text10>
                <Stack spacing={2}>
                  {archivedFieldOptions.map((f) => (
                    <Checkbox
                      key={f.key}
                      isChecked={selected.has(f.key)}
                      onChange={() => toggle(f.key)}
                    >
                      {f.label}
                    </Checkbox>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <CDS.Button intent={CDS.Button.intent.secondary} variant={CDS.Button.variant.ghost} onClick={onClose}>
              Cancel
            </CDS.Button>
            <CDS.Button
              intent={CDS.Button.intent.indigo}
              variant={CDS.Button.variant.solid}
              leftIcon={{ customIcon: <FiDownload /> }}
              onClick={handleDownload}
              disabled={!canDownload}
            >
              Download
            </CDS.Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
